import { CartItem, CartItemModifier, CartItemModifierWithInternalName } from '@/domain/cart/types';
import { BuiltPizza, PizzaIngredient, PizzaIngredientOption } from '@/builders/pizza/dataTransformers/builderTypes';
import { Product } from '@/domain/product/types';
import { PIZZA, PRODUCT, SIZE } from '@/domain/constants';
import { getSelectedPizzaIngredient } from '@/cart/helpers/getSelectedPizzaIngredients';
import Portion from '@/common/Portion';
import { getPlacement } from '@/cart/helpers/getPlacement';
import {
  isCheese, isFinisher, isMeat, isSauce, isSize, isVeggie
} from '@/builders/pizza/identifiers';
import { CurrentStep, DealRecipe, SelectedRecipe } from '@/builders/deals/slice/dealTypes';
import { AddableCartItemKinds } from '@/api/phdApiV2Client/request.types';
import SelectedBy from '@/common/SelectedBy';
import { getGlobalId } from '@/cart/helpers/getGlobalId';
import { IsCyo } from '@/menu/pizza/pizzaMenuTypes';
import { getOrInitializeOptimizely } from '../../../optimizely/optimizely';

const parseCartItemToBuiltPizza = (pizza: CartItem): BuiltPizza => {
  const modifiers = pizza?.modifiers?.map(getSelectedPizzaIngredient).flatMap((item) => item);

  const ingredients = modifiers?.map((item: CartItemModifierWithInternalName) => {
    const ingredient: PizzaIngredient = {
      id: item.id,
      name: item.name,
      type: item.type,
      selectedBy: SelectedBy.USER,
      internalName: item.internalName
    };

    if (item?.quantities && (isMeat(item) || isVeggie(item))) {
      ingredient.portion = item?.quantities?.includes(2) ? Portion.EXTRA : Portion.REGULAR;
      ingredient.placement = getPlacement(item?.quantities);
    }

    return ingredient;
  });

  return {
    id: pizza.id,
    name: pizza.name,
    type: PIZZA,
    isCYO: IsCyo.UNKNOWN,
    quantity: pizza.quantity,
    price: undefined,
    kind: AddableCartItemKinds.BUILT_PIZZA,
    finisher: ingredients?.find(isFinisher) || null,
    finishers: ingredients?.filter(isFinisher) || null,
    cheese: ingredients?.find(isCheese) || null,
    sauce: ingredients?.find(isSauce) || null,
    crust: ingredients?.find((item) => isSize(item) && item.type !== SIZE) || null,
    size: ingredients?.find((item) => isSize(item) && item.type === SIZE) || null,
    meatToppings: ingredients?.filter(isMeat),
    veggieToppings: ingredients?.filter(isVeggie),
    specialInstructions: pizza.specialInstructions || ''
  };
};

export const parseRecipeToBuiltPizza = (recipeId: string, recipes: DealRecipe[], sizeOverride?: PizzaIngredientOption): BuiltPizza | null => {
    const recipe = recipes?.find((r) => r.id === recipeId);
    if (!recipe) return null;

    const bugfixForQuickAddFinisher = getOrInitializeOptimizely()?.isFeatureEnabled('bug_dtg-1752-quick-add-finisher');
  
    const selectedSize = sizeOverride ?? recipe.pizzaBuilderOptions?.sizes?.find((size) => size.selected);
    let selectedCrust = recipe.pizzaBuilderOptions?.crusts?.find((crust) => crust.selected);
    if (sizeOverride) {
      const crustForOverrideSize = recipe.pizzaBuilderOptions?.crusts?.find((crust) => crust.sizeId === selectedSize?.id);
      if (crustForOverrideSize) {
        selectedCrust = crustForOverrideSize;
      }
    } else if ((recipe.pizzaBuilderOptions?.crusts?.filter((crust) => crust.selected) ?? []).length > 1) {
      // DTG-1364 - use sizeId to pick crust because multiple crusts in redux store have selected = true (which is a bug)
      selectedCrust = recipe.pizzaBuilderOptions?.crusts?.find((crust) => crust.sizeId === selectedSize?.id && crust.selected);
    }
    
    const selectedCheese = recipe.pizzaBuilderOptions?.cheeses?.find((cheese) => cheese.selected)?.portions?.find((portion) => portion.selected);
    const selectedSauce = recipe.pizzaBuilderOptions?.sauces?.find((sauce) => sauce.selected)?.portions?.find((portion) => portion.selected);
    const selectedMeatToppings = recipe.pizzaBuilderOptions?.meatToppings?.filter((topping) => topping.selected);
    const selectedVeggieToppings = recipe.pizzaBuilderOptions?.veggieToppings?.filter((topping) => topping.selected);
    const selectedFinisher = recipe.pizzaBuilderOptions?.finishers?.find((finisher) => finisher.selected);
    const selectedBy = 'system';
  
    const builtPizza: BuiltPizza = {
      crust: { ...selectedCrust, selectedBy } as PizzaIngredient,
      size: { ...selectedSize, selectedBy } as PizzaIngredient,
      meatToppings: selectedMeatToppings?.map((topping) => ({ ...topping, selectedBy })) as PizzaIngredient[],
      veggieToppings: selectedVeggieToppings?.map((topping) => ({ ...topping, selectedBy })) as PizzaIngredient[],
      cheese: null,
      sauce: null,
      finisher: null,
      finishers: null,
      kind: AddableCartItemKinds.BUILT_PIZZA,
      quantity: 1,
      specialInstructions: '',
      id: recipeId,
      price: recipe.price,
      type: 'PIZZA',
      isCYO: IsCyo.FALSE
    };
  
    if (selectedCheese) {
      builtPizza.cheese = { ...selectedCheese, selectedBy } as PizzaIngredient;
    }
  
    if (selectedSauce) {
      builtPizza.sauce = { ...selectedSauce, selectedBy } as PizzaIngredient;
    }
  
    if (selectedFinisher) {
      if(!selectedFinisher?.weightCode && bugfixForQuickAddFinisher) {
        builtPizza.finisher = { ...selectedFinisher, selectedBy, weightCode: selectedFinisher?.weights?.[0] } as PizzaIngredient;
      } else {
        builtPizza.finisher = { ...selectedFinisher, selectedBy } as PizzaIngredient;
      }
    }
  
    return builtPizza;
};

export const parseCartItemToProduct = (product: CartItem): Product => {
  const modifiers = product.modifiers.flatMap(
    (p) => p.modifiers ?? new Array<CartItem | CartItemModifier>()
  );

  return {
    kind: AddableCartItemKinds.PRODUCT,
    type: PRODUCT,
    id: product.id,
    name: product.name,
    description: product.description,
    quantity: product.quantity,
    sodiumWarning: !!product.sodiumWarning,
    selectedModifiers: [...product.modifiers, ...modifiers].map((item) => ({
      id: item.id,
      name: item.name,
      type: item.type,
      sodiumWarning: !!item.sodiumWarning
    })),
    variantCode: product.variantCode
  };
};

const mountRecipe = (item: BuiltPizza | Product, stepId: string, index: number): SelectedRecipe => ({
  item,
  stepId,
  index: index || 0
});

export const getCurrentStep = (recipes: SelectedRecipe[]): Partial<CurrentStep> => ({
  recipeId: getGlobalId(recipes[0].item.id),
  recipeType: recipes[0].item.type,
  index: 0
});

export const getUserRecipes = (
  steps: CartItemModifierWithInternalName[]
): Array<SelectedRecipe> => {
  const products = steps
    .map((step) =>({
      ...step.modifiers?.[0],
      specialInstructions: step.specialInstructions
    } as CartItem))
    .filter(Boolean);

  const isPizza = (item: CartItem) => item && item.type === PIZZA;

  return products.map((item, index) => {
    const product = isPizza(item)
      ? parseCartItemToBuiltPizza(item)
      : parseCartItemToProduct(item);

    return mountRecipe(product, steps?.[index].id, index);
  });
};
